import isEmpty from "lodash/isEmpty";
import { useEffect, useMemo, useState } from "react";

interface ElectronAPI {
  changeCommunity: (data: any) => void;
  fetchCookies: (communityUrl: string, authToken: string) => Promise<string[]>;
  logout: () => void;
  onModalClosed: () => void;
  onModalOpened: () => void;
  openUrl: (url: string, cookies: Array<any>) => Promise<boolean>;
  runningOn: string;
  sendNotification: (data: any) => void;
  setAuthTokenToMain: (authToken: string) => Promise<void>;
  setNotificationCount: (count: number, communityId?: number) => void;
  setUnreadMessageCount: (count: number, communityId?: number) => void;
  switchCommunity?: (communityData: any) => Promise<boolean>;
  testNotification?: () => void;
  version?: string;
}

export const isVersionGreaterThanOrEqual = (
  version: string,
  targetVersion: string,
) => {
  const [major, minor, patch] = version.split(".").map(Number);
  const [targetMajor, targetMinor, targetPatch] = targetVersion
    .split(".")
    .map(Number);
  return (
    major > targetMajor ||
    (major === targetMajor &&
      (minor > targetMinor || (minor === targetMinor && patch >= targetPatch)))
  );
};

const MAX_RETRIES = 3;
const RETRY_INTERVAL_MS = 500;

interface UseElectronAPIOutput {
  electronAPI: ElectronAPI | null;
  electronVersion: string | undefined;
  error: string | null;
  isElectron: boolean;
  isLoading: boolean;
  isMacOS: boolean;
  navigateToUrl: (url: string) => Promise<boolean>;
  onModalClosed: () => void;
  onModalOpened: () => void;
  platform: string | undefined;
}

/**
 * Custom React hook to get the electronAPI object from the window,
 * with retries if not immediately available.
 */
export const useElectronAPI = (): UseElectronAPIOutput => {
  const [api, setApi] = useState<ElectronAPI | null>(null);
  const [isElectron, setIsElectron] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout | null = null;

    const findAndSetApi = (retriesLeft: number) => {
      if (!isMounted) {
        return; // Component unmounted, stop.
      }

      const electronAPIInstance = window?.electronAPI;

      if (!isEmpty(electronAPIInstance)) {
        console.warn("Electron API found by hook");
        setApi(electronAPIInstance as ElectronAPI);
        setIsElectron(true);
        setIsLoading(false);
        setError(null);
        return; // API found, stop.
      }

      if (retriesLeft > 0) {
        timeoutId = setTimeout(
          () => findAndSetApi(retriesLeft - 1),
          RETRY_INTERVAL_MS,
        );
      } else {
        // No retries left, API not found.
        setError("Electron API not available.");
        setIsLoading(false);
      }
    };

    findAndSetApi(MAX_RETRIES);

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const navigateToUrl = async (path: string) => {
    if (!api) {
      return false;
    }

    const urlToRedirect = `${window.location.origin}${path}`;

    return api.openUrl(urlToRedirect, []);
  };

  const isMacOS = useMemo(() => api?.runningOn === "darwin", [api]);

  const onModalOpened = () => {
    if (api) {
      api.onModalOpened?.();
    }
  };

  const onModalClosed = () => {
    if (api) {
      api.onModalClosed?.();
    }
  };

  return {
    electronAPI: api,
    isLoading,
    error,
    isElectron,
    navigateToUrl,
    electronVersion: api?.version,
    platform: api?.runningOn,
    isMacOS,
    onModalOpened,
    onModalClosed,
  };
};
